import React from 'react';
import AppIcon from 'components/AppIcon';

interface AppLoaderProps {
  variant?: 'primary' | 'property' | 'minimal' | 'elegant';
  message?: string;
  size?: 'sm' | 'md' | 'lg';
}

const AppLoader: React.FC<AppLoaderProps> = ({ variant = 'primary', message = 'Loading...', size = 'md' }) => {
  const getSizeClasses = () => {
    switch (size) {
      case 'sm':
        return 'w-8 h-8';
      case 'md':
        return 'w-12 h-12';
      case 'lg':
        return 'w-16 h-16';
      default:
        return 'w-12 h-12';
    }
  };

  const renderPropertyLoader = () => (
    <div className="flex flex-col items-center justify-center min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
      <div className="relative">
        {/* Logo prominently displayed */}
        <div className="mb-6">
          <div className="w-20 h-20 mx-auto bg-white rounded-2xl flex items-center justify-center shadow-xl p-3">
            <img src="/assets/images/logo.png" alt="ForecastInn Logo" className="w-full h-full object-contain" />
          </div>
        </div>

        {/* Property Building Animation */}
        <div className="relative">
          <div className="w-24 h-32 bg-gradient-to-t from-blue-600 to-blue-500 rounded-t-lg shadow-2xl">
            {/* Windows */}
            <div className="grid grid-cols-3 gap-1 p-2">
              {[...Array(9)].map((_, i) => (
                <div
                  key={i}
                  className="w-4 h-4 bg-yellow-300 rounded-sm animate-pulse"
                  style={{ animationDelay: `${i * 0.2}s` }}
                />
              ))}
            </div>
          </div>

          {/* Entrance */}
          <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 w-8 h-12 bg-amber-600 rounded-t-lg">
            <div className="w-2 h-2 bg-yellow-400 rounded-full mx-auto mt-2 animate-ping"></div>
          </div>

          {/* Stars around property */}
          <div className="absolute -top-4 -left-4 text-yellow-400 animate-bounce">
            <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
              <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
            </svg>
          </div>

          <div className="absolute -top-2 -right-6 text-yellow-400 animate-bounce" style={{ animationDelay: '0.5s' }}>
            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
              <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
            </svg>
          </div>
        </div>
      </div>

      {/* Loading Text with Animation */}
      <div className="mt-8 text-center">
        <h2 className="text-2xl font-bold text-gray-800 mb-2">ForecastInn</h2>
        <div className="flex items-center justify-center space-x-1">
          <span className="text-lg text-gray-600">{message}</span>
          <div className="flex space-x-1 ml-2">
            <div className="w-2 h-2 bg-blue-500 rounded-full animate-bounce"></div>
            <div className="w-2 h-2 bg-blue-500 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
            <div className="w-2 h-2 bg-blue-500 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
          </div>
        </div>
      </div>
    </div>
  );

  const renderPrimaryLoader = () => (
    <div className="flex flex-col items-center justify-center min-h-screen">
      <div className="relative mb-4">
        {/* Logo with spinning ring */}
        <div
          className={`${getSizeClasses()} border-4 border-gray-200 border-t-blue-600 rounded-full animate-spin`}
        ></div>

        {/* Logo in center */}
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="w-8 h-8 bg-white rounded-full flex items-center justify-center shadow-sm p-1">
            <img src="/assets/images/logo.png" alt="ForecastInn Logo" className="w-full h-full object-contain" />
          </div>
        </div>
      </div>

      <p className="mt-4 text-gray-600 animate-pulse">{message}</p>
    </div>
  );

  const renderMinimalLoader = () => (
    <div className="flex items-center justify-center p-8">
      <div className="flex space-x-2">
        <div className="w-3 h-3 bg-blue-600 rounded-full animate-bounce"></div>
        <div className="w-3 h-3 bg-blue-600 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
        <div className="w-3 h-3 bg-blue-600 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
      </div>
    </div>
  );

  const renderElegantLoader = () => (
    <div className="min-h-screen bg-gradient-to-br from-background via-surface to-gray-50 flex items-center justify-center p-4">
      <div className="max-w-md mx-auto text-center">
        {/* Main Logo/Icon */}
        <div className="relative mb-8">
          <div className="w-24 h-24 mx-auto bg-gradient-to-br from-primary/10 to-primary/20 rounded-2xl flex items-center justify-center shadow-lg backdrop-blur-sm border border-primary/10">
            <div className="w-16 h-16 bg-white rounded-xl flex items-center justify-center shadow-inner p-2">
              <img src="/assets/images/logo.png" alt="ForecastInn Logo" className="w-full h-full object-contain" />
            </div>
          </div>

          {/* Floating elements */}
          <div className="absolute -top-2 -right-2 w-6 h-6 bg-primary/20 rounded-full animate-pulse"></div>
          <div className="absolute -bottom-1 -left-3 w-4 h-4 bg-secondary/30 rounded-full animate-pulse delay-1000"></div>
          <div className="absolute top-4 -left-4 w-3 h-3 bg-accent/40 rounded-full animate-pulse delay-500"></div>
        </div>

        {/* Brand */}
        <div className="space-y-3 mb-8">
          <h1 className="text-3xl font-bold text-text-primary font-heading">ForecastInn</h1>
          <div className="flex items-center justify-center space-x-2 text-primary">
            <AppIcon name="Sparkles" size={16} />
            <span className="text-sm font-medium tracking-wide">Revenue Intelligence Platform</span>
          </div>
        </div>

        {/* Loading Message */}
        <div className="space-y-4 mb-8">
          <p className="text-text-secondary">{message}</p>

          {/* Progress indicators */}
          <div className="flex justify-center space-x-2">
            <div className="w-2 h-2 bg-primary rounded-full animate-bounce"></div>
            <div className="w-2 h-2 bg-primary/70 rounded-full animate-bounce delay-100"></div>
            <div className="w-2 h-2 bg-primary/40 rounded-full animate-bounce delay-200"></div>
          </div>
        </div>

        {/* Feature hints */}
        <div className="grid grid-cols-3 gap-3 max-w-xs mx-auto">
          <div className="bg-surface/60 backdrop-blur-sm border border-border/50 rounded-lg p-3 hover:bg-surface/80 transition-all duration-500">
            <AppIcon name="BarChart3" size={20} className="text-primary mx-auto mb-1" />
            <p className="text-xs text-text-secondary">Analytics</p>
          </div>
          <div className="bg-surface/60 backdrop-blur-sm border border-border/50 rounded-lg p-3 hover:bg-surface/80 transition-all duration-500 delay-100">
            <AppIcon name="Brain" size={20} className="text-primary mx-auto mb-1" />
            <p className="text-xs text-text-secondary">AI Insights</p>
          </div>
          <div className="bg-surface/60 backdrop-blur-sm border border-border/50 rounded-lg p-3 hover:bg-surface/80 transition-all duration-500 delay-200">
            <AppIcon name="TrendingUp" size={20} className="text-primary mx-auto mb-1" />
            <p className="text-xs text-text-secondary">Forecasting</p>
          </div>
        </div>

        {/* Loading bar */}
        <div className="mt-8">
          <div className="w-48 h-1 bg-border/30 rounded-full mx-auto overflow-hidden">
            <div className="h-full bg-gradient-to-r from-primary to-secondary rounded-full animate-pulse loading-bar"></div>
          </div>
        </div>
      </div>
    </div>
  );

  switch (variant) {
    case 'property':
      return renderPropertyLoader();
    case 'minimal':
      return renderMinimalLoader();
    case 'elegant':
      return renderElegantLoader();
    default:
      return renderPrimaryLoader();
  }
};

export default AppLoader;
